import React, { useState, useEffect, useRef, useCallback } from "react";
import wiomLogo from "../assets/svgs/wiom_logo_new.svg";
import { isPincodeServiceable } from "../data/serviceablePincodes";
import "./NewHomePageV1.css";
import "./NewHomePageV2.css";

// API Configuration
const API_BASE_URL = process.env.NODE_ENV === "production" 
  ? "https://lead-service.i2e1.in" 
  : "https://lead-service.qa.i2e1.in";

// Section configuration for GA tracking
const SECTIONS_CONFIG = [
  { id: "hero", name: "रिचार्ज-वाला घर का नेट", threshold: 0.3 },
  { id: "content", name: "आज इंटरनेट का तरीका बदल गया है।", threshold: 0.3 },
  { id: "how_it_works", name: "कैसे काम करता है?", threshold: 0.3 },
  { id: "pincode_check", name: "देखिये आपके एरिया में लाइव है या नहीं", threshold: 0.3 },
];

// Fire GA4 + Facebook Pixel custom event for section scroll
const fireScrollEvent = (sectionId, sectionName) => {
  if (typeof window !== "undefined") {
    // GA4 Event
    if (window.gtag) {
      window.gtag("event", "section_view", {
        section_name: sectionName,
      });
    }
    // Facebook Pixel Event - Use standard ViewContent event
    if (window.fbq) {
      window.fbq("track", "ViewContent", {
        content_name: sectionName,
        content_category: "Section",
      });
    }
  }
};

// Fire GA4 + Facebook Pixel event for lead generation
const fireLeadEvent = () => {
  if (typeof window !== "undefined") {
    // GA4 Event
    if (window.gtag) {
      window.gtag("event", "generate_lead", {
        lead_source: "website",
      });
    }
    // Facebook Pixel Event
    if (window.fbq) {
      window.fbq("track", "Lead", {
        lead_source: "website",
      });
    }
  }
};

// Fire GA4 + Facebook Pixel event for pincode submitted
const firePincodeSubmittedEvent = (pincode, serviceable) => {
  if (typeof window !== "undefined") {
    const serviceableStr = serviceable ? "TRUE" : "FALSE";
    // GA4 Event
    if (window.gtag) {
      window.gtag("event", "pincodeSubmitted", {
        pincode: pincode,
        serviceable: serviceableStr,
      });
    }
    // Facebook Pixel Event
    if (window.fbq) {
      window.fbq("track", "pincodeSubmitted", {
        pincode: pincode,
        serviceable: serviceableStr,
      });
    }
  }
};

// Fire GA4 + Facebook Pixel event for app download click
const fireAppDownloadedEvent = (pincode) => {
  if (typeof window !== "undefined") {
    // GA4 Event
    if (window.gtag) {
      window.gtag("event", "appDownloaded", {
        pincode: pincode || "",
      });
    }
    // Facebook Pixel Event
    if (window.fbq) {
      window.fbq("track", "appDownloaded", {
        pincode: pincode || "",
      });
    }
  }
};

// Get URL params as a string
const getUtmParams = () => {
  if (typeof window !== "undefined") {
    const searchParams = window.location.search;
    return searchParams.startsWith('?') ? searchParams.slice(1) : searchParams;
  }
  return "";
};

// Play Store base URL
const PLAY_STORE_BASE =
  "https://play.google.com/store/apps/details?id=com.i2e1.wiom_gold&hl=en_IN";

// Get app store download URL with UTM params from current page
const getAppStoreUrl = () => {
  const qs = getUtmParams();
  if (!qs) return PLAY_STORE_BASE;
  return `${PLAY_STORE_BASE}&${qs}`;
};

// Submit lead to API
const submitLead = async (phone, utmParams, pincode) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/lead/campaign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phone: phone,
        campaign: '26-Jan-2026',
        utmParams: utmParams,
        pincode: pincode,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { 
        success: false, 
        message: data.message || "API request failed",
        status: data.status,
        customCode: data.customCode
      };
    }

    return { success: true, id: data.id };
  } catch (error) {
    console.error("Lead submission error:", error);
    return { success: false, message: "कुछ गलत हो गया। कृपया फिर से कोशिश करें।" };
  }
};

const NewHomePageV2 = () => {
  const [pincode, setPincode] = useState("");
  const [pincodeError, setPincodeError] = useState("");
  const [pincodeStatus, setPincodeStatus] = useState(null); // 'available', 'unavailable', or null
  const [isChecking, setIsChecking] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Refs for section tracking
  const sectionRefs = useRef({});
  const trackedSections = useRef(new Set());

  // Assign ref to section
  const setSectionRef = useCallback((id) => (el) => {
    sectionRefs.current[id] = el;
  }, []);

  // IntersectionObserver for GA tracking
  useEffect(() => {
    const observers = [];

    SECTIONS_CONFIG.forEach(({ id, name, threshold }) => {
      const element = sectionRefs.current[id];
      if (!element) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !trackedSections.current.has(id)) {
            trackedSections.current.add(id);
            fireScrollEvent(id, name);
          }
        },
        { threshold }
      );

      observer.observe(element);
      observers.push({ observer, element });
    });

    return () => {
      observers.forEach(({ observer, element }) => {
        observer.unobserve(element);
      });
    };
  }, []);

  const validatePincode = (value) => {
    const digitsOnly = value.replace(/\D/g, "");
    return digitsOnly.slice(0, 6);
  };

  const validatePhone = (value) => {
    const digitsOnly = value.replace(/\D/g, "");
    return digitsOnly.slice(0, 10);
  };

  const handlePincodeChange = (e) => {
    const validated = validatePincode(e.target.value);
    setPincode(validated);
    setPincodeError("");
    setPincodeStatus(null);
  };

  const handlePhoneChange = (e) => {
    const validated = validatePhone(e.target.value);
    setPhone(validated);
    setPhoneError("");
  };

  const handlePincodeCheck = (e) => {
    e.preventDefault();

    if (pincode.length !== 6) {
      setPincodeError("6 अंकों का पिनकोड डालें");
      return;
    }

    setIsChecking(true);
    setPincodeError("");

    // Check if pincode is serviceable
    const isServiceable = isPincodeServiceable(pincode);
    setPincodeStatus(isServiceable ? 'available' : 'unavailable');

    // Fire analytics event for pincode submitted
    firePincodeSubmittedEvent(pincode, isServiceable);

    // API is only called when user submits phone (Notify Me)
    setIsChecking(false);
  };

  const handleNotifyMe = async (e) => {
    e.preventDefault();

    if (phone.length !== 10) {
      setPhoneError("10 अंकों का नंबर डालें");
      return;
    }

    setIsSubmitting(true);
    setPhoneError("");

    // Call the API with phone, UTM params and pincode (same as FormSection)
    const utmParams = getUtmParams();
    const result = await submitLead(phone, utmParams, pincode);

    if (result.success) {
      // Fire analytics events for lead generation
      fireLeadEvent();
      if (result.id) {
        sessionStorage.setItem("wiom_lead_id", result.id.toString());
      }
      setSubmitted(true);
    } else {
      console.error("API Error:", result.message);
      setPhoneError(result.message || "कुछ गलत हो गया। कृपया फिर से कोशिश करें।");
    }

    setIsSubmitting(false);
  };

  const scrollToCheck = () => {
    const checkSection = document.getElementById("pincode-check-section");
    if (checkSection) {
      checkSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <main className="new-home-page-v2" style={{ 
      fontFamily: "'Noto Sans Devanagari'",
      backgroundColor: "hsl(0, 0%, 97%)",
      minHeight: "100vh"
    }}>
      {/* Header with Logo */}
      <header style={{
        padding: "0 1.5rem",
        position: "sticky",
        top: "20px",
        backgroundColor: "hsl(0, 0%, 97%)",
        zIndex: 100,
        // borderBottom: "1px solid rgba(0, 0, 0, 0.05)"
      }}>
        <img 
          src={wiomLogo} 
          alt="Wiom Logo" 
          style={{ height: "6.5rem" }}
        />
      </header>

      {/* Hero Section */}
      <section 
        ref={setSectionRef("hero")}
        style={{
          padding: "0 1.5rem",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - 30rem)",
          paddingTop: "4rem",
          paddingBottom: "4rem"
        }}
      >
        <h1 className="hero-title" style={{
          fontFamily: "'Noto Sans Devanagari'",
          fontSize: "5rem",
          fontWeight: 900,
          color: "#000000",
          margin: "0 0 0.25rem 0",
          lineHeight: 1.2
        }}>
          रिचार्ज-वाला
        </h1>
        <h3 className="hero-subtitle" style={{
          fontFamily: "'Noto Sans Devanagari'",
          fontSize: "3.75rem",
          fontWeight: 700,
          color: "#000000",
          margin: "0 0 1rem 0",
          lineHeight: 1.2
        }}>
          घर का नेट  
        </h3>
        <div style={{
          width: "8rem",
          height: "0.25rem",
          backgroundColor: "#E7D96A",
          margin: "0 0 2rem 0"
        }} />

        <p className="hero-desc" style={{
          fontFamily: "'Noto Sans Devanagari'",
          fontSize: "2.5rem",
          fontWeight: 300,
          color: "#000000",
          margin: "0 0 0.5rem 0",
          lineHeight: 1.5
        }}>
          इंटरनेट जो रिचार्ज से चलता है।
        </p>
        <p className="hero-desc" style={{
          fontFamily: "'Noto Sans Devanagari'",
          fontSize: "2.5rem",
          fontWeight: 300,
          color: "#000000",
          margin: "0 0 3rem 0",
          lineHeight: 1.5
        }}>
          जब चाहो। जितने दिन चाहो।
        </p>

        <button
          className="hero-link"
          onClick={scrollToCheck}
          style={{
            fontFamily: "'Noto Sans Devanagari'",
            fontSize: "2rem",
            fontWeight: 400,
            color: "#000000",
            background: "transparent",
            border: "none",
            textDecoration: "underline",
            cursor: "pointer",
            padding: "0.5rem",
            marginTop: "1rem"
          }}
        >
          <span style={{ textDecoration: "underline" }}>देखिये आपके एरिया में लाइव है या नहीं</span>
        </button>
      </section>

      {/* Content Section */}
      <section 
        ref={setSectionRef("content")}
        style={{
          padding: "4rem 1.5rem",
          textAlign: "center",
          maxWidth: "32rem",
          margin: "0 auto"
        }}
      >
        <h2 className="section-heading" style={{
          fontFamily: "'Noto Sans Devanagari'",
          fontSize: "2.5rem",
          fontWeight: 500,
          color: "#000000",
          margin: "0 0 3rem 0",
          lineHeight: 1.375,
        //   textDecoration: "underline"
        }}>
          आज इंटरनेट का तरीका बदल गया है।
        </h2>

        <ul style={{
          listStyle: "none",
          padding: 0,
          margin: "0 0 1.5rem 0",
          textAlign: "center",
          maxWidth: "32rem",
          marginLeft: "auto",
          marginRight: "auto"
        }}>
          <li className="section-list-item" style={{
            fontFamily: "'Noto Sans Devanagari'",
            fontSize: "2rem",
            fontWeight: 400,
            color: "#000000",
            marginBottom: "1rem",
            lineHeight: 1.625
          }}>
            • हर दिन इंटरनेट की ज़रूरत अलग होती है
          </li>
          <li className="section-list-item" style={{
            fontFamily: "'Noto Sans Devanagari'",
            fontSize: "2rem",
            fontWeight: 400,
            color: "#000000",
            marginBottom: "1rem",
            lineHeight: 1.625
          }}>
            • कंट्रोल आपके पास होना चाहिए
          </li>
        </ul>

        <p className="section-text" style={{
          fontFamily: "'Noto Sans Devanagari'",
          fontSize: "2.5rem",
          fontWeight: 400,
          color: "#000000",
          margin: "3.5rem 0 0.5rem 0",
          lineHeight: 1.625
        }}>
          इसलिए:
        </p>
        <p className="section-highlight" style={{
          fontFamily: "'Noto Sans Devanagari'",
          fontSize: "2.5rem",
          fontWeight: 500,
          color: "#000000",
          margin: "0 0 3rem 0",
          lineHeight: 1.375
        }}>
          रिचार्ज-वाला घर का नेट
        </p>

        <div style={{
          width: "100%",
          height: "1px",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
          margin: "3rem 0"
        }} />

        <div ref={setSectionRef("how_it_works")}>
          <h2 className="how-it-works-heading" style={{
            fontFamily: "'Noto Sans Devanagari'",
            fontSize: "3.65rem",
            fontWeight: 600,
            color: "#000000",
            margin: "8rem 0 3rem 0",
            lineHeight: 1.375,
            textAlign: "center"
          }}>
            कैसे काम करता है?
          </h2>

          <div style={{
            display: "flex",
            justifyContent: "center"
          }}>
          <ol style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            textAlign: "left"
          }}>
            <li className="how-it-works-item" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2rem",
              fontWeight: 300,
              color: "#000000",
              marginBottom: "1.5rem",
              lineHeight: 1.625
            }}>
              <span style={{
                fontWeight: 400,
                color: "rgba(0, 0, 0, 0.5)",
                marginRight: "0.5rem"
              }}>
                1.
              </span>
              जब ज़रूरत हो, रिचार्ज करो
            </li>
            <li className="how-it-works-item" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2rem",
              fontWeight: 300,
              color: "#000000",
              marginBottom: "1.5rem",
              lineHeight: 1.625,
              marginLeft: "-2rem"
            }}>
              <span style={{
                fontWeight: 400,
                color: "rgba(0, 0, 0, 0.5)",
                marginRight: "0.5rem"
              }}>
                2.
              </span>
              जितने दिन चलाओ, उतना ही चले
            </li>
            <li className="how-it-works-item" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2rem",
              fontWeight: 300,
              color: "#000000",
              marginBottom: "1.5rem",
              lineHeight: 1.625
            }}>
              <span style={{
                fontWeight: 400,
                color: "rgba(0, 0, 0, 0.5)",
                marginRight: "0.5rem"
              }}>
                3.
              </span>
              रिचार्ज नहीं, तो नेट बंद
            </li>
          </ol>
          </div>
        </div>
      </section>

      {/* Pincode Check Section */}
      <section 
        id="pincode-check-section"
        ref={setSectionRef("pincode_check")}
        style={{
          padding: "4rem 1.5rem",
          textAlign: "center",
          width: "90vw",
          maxWidth: "48rem",
        //   ßmaxWidth: "100%",
          margin: "0 auto",
          backgroundColor: "hsl(0, 0%, 97%)"
        }}
      >
        {pincodeStatus === null && (
          <>
            <p className="pincode-label" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2rem",
              fontWeight: 400,
              color: "rgba(0, 0, 0, 0.5)",
              margin: "0 0 0.5rem 0",
              lineHeight: 1.5
            }}>
              अगर आप चेक करना चाहते हैं:
            </p>
            <h2 className="pincode-heading" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2.5rem",
              fontWeight: 600,
              color: "#000000",
              margin: "0 0 3rem 0",
              lineHeight: 1.4
            }}>
              देखिये आपके एरिया में लाइव है या नहीं
            </h2>

            <form onSubmit={handlePincodeCheck} style={{ marginTop: "2rem" }}>
              <div style={{ marginBottom: "1.5rem" }}>
                <input
                  className="form-input"
                  type="tel"
                  value={pincode}
                  onChange={handlePincodeChange}
                  placeholder="पिनकोड (6 अंक)"
                  style={{
                    width: "100%",
                    padding: "1rem 1.25rem",
                    border: "1px solid #e0e0e0",
                    borderRadius: "0.25rem",
                    fontFamily: "'Noto Sans Devanagari'",
                    fontSize: "2rem",
                    color: "#000000",
                    outline: "none",
                    boxSizing: "border-box",
                    backgroundColor: "#ffffff"
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                {pincodeError && (
                  <p style={{
                    marginTop: "0.5rem",
                    fontSize: "2rem",
                    color: "#dc2626",
                    fontFamily: "'Noto Sans Devanagari'"
                  }}>
                    {pincodeError}
                  </p>
                )}
              </div>

              <button
                className="form-button"
                type="submit"
                disabled={isChecking}
                style={{
                  width: "100%",
                  padding: "1rem 1.25rem",
                  border: pincode.length === 6 ? "1px solid #E7D96A" : "1px solid #e0e0e0",
                  borderRadius: "0.25rem",
                  fontFamily: "'Noto Sans Devanagari'",
                  fontSize: "2rem",
                  fontWeight: pincode.length === 6 ? 500 : 400,
                  cursor: isChecking ? "not-allowed" : "pointer",
                  backgroundColor: pincode.length === 6 ? "#E7D96A" : "#ffffff",
                  color: pincode.length === 6 ? "#000000" : "rgba(0, 0, 0, 0.5)",
                  transition: "all 0.3s ease",
                  boxSizing: "border-box"
                }}
              >
                {isChecking ? "Check हो रहा है..." : "उपलब्धता देखें"}
              </button>
            </form>
          </>
        )}

        {pincodeStatus === 'available' && (
          <div>
            <h2 className="result-heading" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2.75rem",
              fontWeight: 700,
              color: "#000000",
              margin: "0 0 1.5rem 0",
              lineHeight: 1.375
            }}>
              आपके एरिया में Wiom लाइव है!
            </h2>
            <p className="result-text" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2rem",
              fontWeight: 400,
              color: "rgba(26, 26, 26, 0.7)",
              margin: "0 0 2rem 0",
              lineHeight: 1.5
            }}>
              रिचार्ज-वाला घर का नेट
            </p>
            <a
              className="form-button"
              href={getAppStoreUrl()}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                padding: "1rem 2rem",
                backgroundColor: "#E7D96A",
                color: "#000000",
                textDecoration: "none",
                borderRadius: "0.5rem",
                fontFamily: "'Noto Sans Devanagari'",
                fontSize: "2rem",
                fontWeight: 500,
                border: "1px solid #E7D96A",
                transition: "all 0.3s ease"
              }}
              onClick={() => fireAppDownloadedEvent(pincode)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#d4c85f";
                e.target.style.transform = "scale(1.04)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#E7D96A";
                e.target.style.transform = "scale(1)";
              }}
            >
              Play Store से डाउनलोड करें
            </a>
          </div>
        )}

        {pincodeStatus === 'unavailable' && !submitted && (
          <div>
            <h2 className="result-heading" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2.75rem",
              fontWeight: 700,
              color: "#000000",
              margin: "0 0 1.5rem 0",
              lineHeight: 1.375
            }}>
              अभी आपके एरिया में Wiom लाइव नहीं है।
            </h2>
            {/* <p className="result-text" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2rem",
              fontWeight: 400,
              color: "#000000",
              margin: "0 0 0.5rem 0",
              lineHeight: 1.5
            }}>
              रिचार्ज-वाला घर का नेट
            </p> */}
            {/* <p style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2rem",
              fontWeight: 400,
              color: "#000000",
              margin: "0 0 2rem 0",
              lineHeight: 1.5
            }}>
              Wiom का डिफ़ॉल्ट सिस्टम है।
            </p> */}
            <p className="result-text" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2rem",
              fontWeight: 400,
              color: "#000000",
              margin: "0 0 2rem 0",
              lineHeight: 1.5
            }}>
              जैसे ही आपके एरिया में आएगा, हम आपको बता देंगे।
            </p>

            <form onSubmit={handleNotifyMe} style={{ marginTop: "2rem" }}>
              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e0e0e0",
                  borderRadius: "0.5rem",
                  padding: "0.875rem 1rem"
                }}>
                  <span style={{
                    fontFamily: "'Noto Sans Devanagari'",
                    fontSize: "2rem",
                    fontWeight: 400,
                    color: "rgba(26, 26, 26, 0.5)",
                    marginRight: "0.5rem"
                  }}>
                    +91
                  </span>
                  <input
                    className="form-input"
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="मोबाइल नंबर"
                    style={{
                      flex: 1,
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontFamily: "'Noto Sans Devanagari'",
                      fontSize: "2rem",
                      color: "#1a1a1a",
                      padding: 0
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>
                {phoneError && (
                  <p style={{
                    marginTop: "0.5rem",
                    fontSize: "2rem",
                    color: "#dc2626",
                    fontFamily: "'Noto Sans Devanagari'"
                  }}>
                    {phoneError}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: "100%",
                  padding: "0.875rem 1.5rem",
                  border: "none",
                  borderRadius: "0.5rem",
                  fontFamily: "'Noto Sans Devanagari'",
                  fontSize: "2rem",
                  fontWeight: 400,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  backgroundColor: "transparent",
                  color: "#000000",
                  textAlign: "center",
                  transition: "all 0.3s ease"
                }}
              >
                <span style={{ textDecoration: "underline" }}>{isSubmitting ? "सबमिट हो रहा है..." : "मुझे सूचित करें"}</span>
              </button>
            </form>
          </div>
        )}

        {pincodeStatus === 'unavailable' && submitted && (
          <div>
            <h2 className="result-heading" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2.75rem",
              fontWeight: 700,
              color: "#000000",
              margin: "0 0 1.5rem 0",
              lineHeight: 1.375
            }}>
              धन्यवाद!
            </h2>
            <p className="result-text" style={{
              fontFamily: "'Noto Sans Devanagari'",
              fontSize: "2rem",
              fontWeight: 400,
              color: "rgba(26, 26, 26, 0.7)",
              margin: "0",
              lineHeight: 1.5
            }}>
              जैसे ही आपके एरिया में Wiom आएगा, हम आपको सूचित कर देंगे।
            </p>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer style={{
        padding: "3rem 1.5rem",
        textAlign: "center",
        borderTop: "1px solid rgba(0, 0, 0, 0.1)",
        marginTop: "4rem"
      }}>
        <p className="footer-title" style={{
          fontFamily: "'Noto Sans Devanagari'",
          fontSize: "2.5rem",
          fontWeight: 600,
          color: "#000000",
          margin: "0 0 0.5rem 0"
        }}>
          Wiom
        </p>
        <p className="footer-text" style={{
          fontFamily: "'Noto Sans Devanagari'",
          fontSize: "2rem",
          fontWeight: 400,
          color: "#000000",
          margin: "0 0 0.5rem 0"
        }}>
          रिचार्ज-वाला घर का नेट
        </p>
        <p className="footer-text" style={{
          fontFamily: "'Noto Sans Devanagari'",
          fontSize: "2rem",
          fontWeight: 400,
          color: "#000000",
          margin: "0"
        }}>
          जब रिचार्ज, तब नेट।
        </p>
      </footer>
    </main>
  );
};

export default NewHomePageV2;

